import {
  ILlmSubjectInput,
  ILlmSummarizeEntityMutation,
  ILlmSummarizeEntityMutationVariables,
  ILlmSummarizeResponseFieldsFragment,
  ILlmSummarizeSectionsInput,
  ILlmTempImagePreUploadQuery,
  ILlmTempImagePreUploadQueryVariables,
  LlmSubjectKind,
} from '../../utils/apollo/graphqlSchema';
import { useCallback } from 'react';
import toastMessage from '../../utils/toastMessage';
import { useLazyQuery, useMutation } from '@apollo/client';
import { LLM_TEMP_IMAGE_PRE_UPLOAD } from '../../utils/apollo/llm/queries';
import { LLM_SUMMARIZE_ENTITY } from '../../utils/apollo/llm/mutations';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import createAndUploadScreenshot from '../../utils/screenshot/createAndUploadScreenshot';
import { MaybeNil } from '../../utils/interfaces';
import { assertNotNil } from '../../utils/types';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import {
  aiResponseReturned,
  llmSummarizeErrorContexts,
} from '../../utils/analytics/events/llm/events';
import { formErrorViewed } from '../../utils/analytics/events/error/events';

export interface ILlmExplainInternalInput {
  clientRawData: MaybeNil<string>;
  prompt: MaybeNil<string>;
  takeScreenshot: boolean;
}

interface IProps {
  divElementId: string;
  schemaId: string | null;
  subjectKind: LlmSubjectKind;
  subjectId: MaybeNil<string>;
  sections: ILlmSummarizeSectionsInput;
  loading: boolean;
  setLoading: (b: boolean) => void;
  onExplainComplete: (resp: ILlmSummarizeResponseFieldsFragment) => void;
}

/**
 * In order to generate and send screenshots from the current report, we use a static div element id
 * to find the specific DOM element wrapping the chart and use that with html2canvas.
 * This should be used in exactly two places:
 *   * the place where we set the id of the element wrapping the chart (can be moved, e.g. initially it also
 *   covered the Next Steps and Data Table, now does not).
 *   * the place where we call useExplainEntityWithAi - somewhere near the "explain with AI" button.
 * @param analysisId - the analysisId is included in the generated DOM element ID.
 */
export const analysisAiScreenshotDivElementId = (analysisId: string) =>
  `analysis-ai-screenshot-wrapper-${analysisId}`;

/**
 * In order to generate and send screenshots from the current dashboard, we use a static div element id
 * to find the specific DOM element wrapping the dashboard view and use that with html2canvas.
 * This should be used in exactly two places:
 *   * the place where we set the id of the element wrapping the Dashboard
 *   * the place where we call useExplainEntityWithAi - in the panel on the right side.
 */
export const dashboardAiScreenshotDivElementId = (dashboardId: string) =>
  `dashboard-ai-screenshot-wrapper-${dashboardId}`;

const debugLogResponse = (
  entity: ILlmSummarizeResponseFieldsFragment | null,
) => {
  if (!entity) {
    console.info('');
    return;
  }
  entity.thread.reverse().forEach((msg) => {
    const attachments = msg.attachments.map(
      (att) => `${att.llmFileId} (${att.originalS3Key})`,
    );
    const attachmentsStr =
      attachments.length === 0
        ? ''
        : `[Attachments: ${attachments.join(',')}]: `;
    console.info(
      `GPT THREAD MESSAGE (${msg.sender}): ${attachmentsStr}Text:`,
      msg.text,
    );
  });
};

/**
 * Helper to return the actual async explain(prompt) function
 * Involves taking a screenshot, uploading it to S3 and then
 * calling Kobol to request a "summary" from the AI
 * @param divElementId - the if of the element to take screenshot of
 * @param schemaId - analysis.schemaId or null. TODO this should eventually go away
 * @param subjectKind - analysis or dashboard
 * @param subjectId - analysis id or dashboard baseId
 * @param sections - a set of sections/flags about what to include in the explain response
 * @param loading - loading state (kept outside of the hook)
 * @param setLoading - call-back to update the loading state
 * @param onExplainComplete - the call-back to call on successful explain
 */
const useExplainEntityWithAiCall = ({
  divElementId,
  schemaId,
  subjectKind,
  subjectId,
  sections,
  loading,
  setLoading,
  onExplainComplete,
}: IProps): ((input: ILlmExplainInternalInput) => void) => {
  const { track } = useAnalytics();

  const trackResponseReceived = useCallback(() => {
    if (subjectId) {
      track(
        aiResponseReturned({
          subjectKind,
          subjectId,
          sections,
        }),
      );
    }
  }, [track, sections, subjectId, subjectKind]);

  const trackErrorReceived = useCallback(
    (message: string) => {
      const context = llmSummarizeErrorContexts[subjectKind];
      track(formErrorViewed({ message, context }));
    },
    [subjectKind, track],
  );

  const handleError = useCallback(
    (message: string) => {
      setLoading(false);
      toastMessage({
        message,
        type: 'error',
      });
    },
    [setLoading],
  );

  const [llmTempImagePreUpload] = useLazyQuery<
    ILlmTempImagePreUploadQuery,
    ILlmTempImagePreUploadQueryVariables
  >(LLM_TEMP_IMAGE_PRE_UPLOAD);

  const [llmSummarizeEntity] = useMutation<
    ILlmSummarizeEntityMutation,
    ILlmSummarizeEntityMutationVariables
  >(LLM_SUMMARIZE_ENTITY);

  const callSummarize = useCallback(
    async (screenshotS3Key: string | null, input: ILlmExplainInternalInput) => {
      assertNotNil(subjectId);
      const subject: ILlmSubjectInput = {
        id: subjectId,
        kind: subjectKind,
        schemaId,
        screenshotS3Key,
      };
      const { prompt, clientRawData } = input;
      const serverResponse = await llmSummarizeEntity({
        variables: {
          input: {
            subject,
            sections,
            prompt: !!prompt ? prompt : null,
            clientRawData: !!clientRawData ? clientRawData : null,
          },
        },
      }).catch((err) => {
        handleError(`Server error: ${err}`);
        return null;
      });
      if (serverResponse === null) {
        return; // error already handled
      }
      const summarizeResponse = serverResponse?.data?.llmSummarizeEntity;
      if (!summarizeResponse) {
        // shouldn't happen
        handleError('Unexpected server response');
        return;
      }
      if (printGraphqlErrors(summarizeResponse.errors)) {
        setLoading(false);
        return;
      }
      debugLogResponse(summarizeResponse.entity);
      if (summarizeResponse.entity) {
        onExplainComplete(summarizeResponse.entity);
        if (summarizeResponse.entity.error) {
          trackErrorReceived(
            summarizeResponse.entity.summary?.text ?? 'Empty response',
          );
        } else {
          trackResponseReceived();
        }
      }
      setLoading(false);
    },
    [
      handleError,
      llmSummarizeEntity,
      onExplainComplete,
      schemaId,
      sections,
      setLoading,
      subjectId,
      subjectKind,
      trackResponseReceived,
      trackErrorReceived,
    ],
  );

  return useCallback(
    async (input: ILlmExplainInternalInput) => {
      if (!subjectId) {
        return;
      }
      if (loading) {
        return;
      }
      setLoading(true);
      if (!input.takeScreenshot) {
        await callSummarize(null, input);
      } else {
        // call preUpload and then upload a screenshot
        const preUploadResult = await llmTempImagePreUpload().catch(() => {
          setLoading(false);
          return null;
        });
        const preUpload = preUploadResult?.data?.llmTempImagePreUpload;
        if (!preUpload) {
          handleError('Failed to get image upload URL');
          return;
        }
        const onUploadComplete = async (success: boolean) => {
          if (!success) {
            handleError('Failed to generate and upload image');
            return;
          }
          await callSummarize(preUpload.objectKey, input);
        };
        await createAndUploadScreenshot({
          divElementId,
          s3UploadUrl: preUpload.url,
          onComplete: onUploadComplete,
        });
      }
    },
    [
      subjectId,
      loading,
      setLoading,
      llmTempImagePreUpload,
      divElementId,
      handleError,
      callSummarize,
    ],
  );
};

export default useExplainEntityWithAiCall;
